import React, { useState, useEffect } from 'react';
import { Box, Typography, InputAdornment } from '@mui/material';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useDebounce } from 'use-lodash-debounce';

import { ROUTES } from '../../../../constants/routes';
import { ReactComponent as SearchIcon } from '../../../../assets/images/search.svg';
import { HEAD_CELLS } from '../../constants/headCell';
import EnhancedTable from '../../../../components/EnhancedTable';
import { CustomSelect } from '../../../../components/CustomSelect';
import { FirstTextSelect } from '../../../../components/FirstTextSelcet';
import { CustomButton } from '../../../../components/CustomButton';
import { CustomInput } from '../../../../components/CustomInput';
import { CustomModal } from '../../../../components/CustomModal';
import { archiveTemplate, clearArchiveTemplateErrors, getMailingTemplates } from '../../../../store/mailing/actions';
import { MAILING_CAMPAIGN_TYPES_FILTER, TEMPLATES_STATUS, MAILING_TYPES_FILTER } from '../../constants';
import { TemplatesTableBody } from '../../components/TemplatesTableBody';
import { getEventDeclensionForm } from '../../../../utils/getEventDeclension';
import { visibleBySectionPermission } from '../../../../utils/visibleBySectionPermission';
import { setUsersPermissions } from '../../../../utils/setUsersPermissions';
import { DISABLED_CONTROL_TOOLTIP, PERMISSIONS } from '../../../../constants/variables';
import { NotFoundItems } from '../../../../components/NotFoundItems';

const styleInput = {
  size: 'small',
  variant: 'outlined'
};

const propsSelect = (name, text, options, value) => ({
  name,
  options,
  value,
  startAdornmentText: <FirstTextSelect text={text} />,
  ...styleInput
});
const orderBy = (property) => {
  switch (property) {
    case 'category':
      return 'campaign_type';
    case 'type':
      return 'mailing_type';
    default:
      return property;
  }
};

export const Templates = () => {
  const dispatch = useDispatch();
  const { templates, fetching, archiveError } = useSelector((state) => state.mailing);
  const { userPermissions, currentUsersPermissions } = useSelector((state) => state.userPermissions);
  const permissions = setUsersPermissions(userPermissions, currentUsersPermissions?.permissions);

  const [filter, setFilter] = useState({
    orderBy: 'created_at',
    orderType: 'desc',
    search: '',
    status: TEMPLATES_STATUS.find((status) => status.value === 'active'),
    campaign_type: MAILING_CAMPAIGN_TYPES_FILTER[0],
    mailing_type: MAILING_TYPES_FILTER[0],
    created_at: '',
    page: 0,
    size: 10,
  });
  const [isSuccessModal, setIsSuccessModal] = useState(false);
  const [curID, setCurID] = useState('');

  const debouncedValue = useDebounce(filter.search, 800);

  const isAllowedToListMailingTemplates = visibleBySectionPermission(permissions, PERMISSIONS.MAILING.ListMailingTemplates.value);
  const isAllowedToCreateMailingTemplates = visibleBySectionPermission(permissions, PERMISSIONS.MAILING.CreateMailingTemplates.value);
  const isAllowedToArchiveMailingTemplates = visibleBySectionPermission(permissions, PERMISSIONS.MAILING.ArchiveMailingTemplates.value);
  const isAllowedToDeleteMailingTemplates = visibleBySectionPermission(permissions, PERMISSIONS.MAILING.DeleteMailingTemplates.value);
  const isAllowedToRetrieveMailingTemplates = visibleBySectionPermission(permissions, PERMISSIONS.MAILING.RetrieveMailingTemplates.value);
  const isAllowedToUpdateMailingTemplates = visibleBySectionPermission(permissions, PERMISSIONS.MAILING.UpdateMailingTemplates.value);

  useEffect(() => {
    dispatch(getMailingTemplates({
      search: filter.search !== '' ? filter.search : undefined,
      status: filter.status.value !== TEMPLATES_STATUS[0].value ? filter.status.value : undefined,
      campaign_type: filter.campaign_type.value === MAILING_CAMPAIGN_TYPES_FILTER[0].value ? undefined : filter.campaign_type.value,
      mailing_type: filter.mailing_type.value === MAILING_TYPES_FILTER[0].value ? undefined : filter.mailing_type.value,
      order_by: filter.orderBy === 'created_at' ? 'data_start' : orderBy(filter.orderBy),
      order_type: filter.orderType,
      size: filter.size,
      page: filter.page + 1,
    }));  // eslint-disable-next-line
  }, [
    debouncedValue,
    filter.status,
    filter.campaign_type,
    filter.mailing_type,
    filter.orderBy,
    filter.orderType,
    filter.size,
    filter.page,
  ]);

  useEffect(() => {
    if (archiveError) {
      setIsSuccessModal(true);
    }
  }, [archiveError]);

  const handlerOnChange = (e, field) => setFilter({ ...filter, page: 0, [field]: e });
  const handleArchiver = () => {
    dispatch(archiveTemplate(curID));
  };

  return (
    <Box id="bonus-list">
      <Typography mb="24px" variant="h1" sx={{ color: 'text.primary' }} component="h1">
        CMC/Email рассылка
      </Typography>
      <Box display="flex" justifyContent="space-between" alignItems="center" m="24px 0px">
        {
          isAllowedToCreateMailingTemplates ? (
            <CustomButton
              component={Link}
              to={ROUTES.MAILING_ADD_TEMPLATE}
              size="large"
              variant="contained"
            >
              Создать рассылку
            </CustomButton>
          ) : (
            <CustomButton
              href='#'
              size="large"
              variant="contained"
              disabled={true}
              tooltip={DISABLED_CONTROL_TOOLTIP}
            >
              Создать рассылку
            </CustomButton>
          )
        }

        <CustomInput
          value={filter.search}
          onChange={(e) => setFilter({ ...filter, search: e.target.value, page: 0 })}
          {...styleInput}
          type="search"
          placeholder="Поиск"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
          disabled={!isAllowedToListMailingTemplates}
          tooltip={!isAllowedToListMailingTemplates ? DISABLED_CONTROL_TOOLTIP : null}
        />
      </Box>

      <Box display="grid" gridTemplateColumns="1fr 1fr 1fr 1fr" gap="16px" data-testid="Grid">
        <CustomSelect
          {...propsSelect('status', 'Статус:', TEMPLATES_STATUS, filter.status)}
          onChange={(e) => handlerOnChange(e, 'status')}
          disabled={!isAllowedToListMailingTemplates}
          tooltip={!isAllowedToListMailingTemplates ? DISABLED_CONTROL_TOOLTIP : null}
        />

        <CustomSelect
            {...propsSelect('campaign_type', 'Категория кампании:', MAILING_CAMPAIGN_TYPES_FILTER, filter.campaign_type)}
            onChange={(e) => handlerOnChange(e, 'campaign_type')}
            disabled={!isAllowedToListMailingTemplates}
            tooltip={!isAllowedToListMailingTemplates ? DISABLED_CONTROL_TOOLTIP : null}
        />

        <CustomSelect
          {...propsSelect('mailing_type', 'Тип рассылки:', MAILING_TYPES_FILTER, filter.mailing_type)}
          onChange={(e) => handlerOnChange(e, 'mailing_type')}
          disabled={!isAllowedToListMailingTemplates}
          tooltip={!isAllowedToListMailingTemplates ? DISABLED_CONTROL_TOOLTIP : null}
        />
      </Box>

      {
        isAllowedToListMailingTemplates ? (
          fetching || templates?.items?.length ? (
            <>
              <Typography color="text.secondary" m="24px 0px">
                Найдено {getEventDeclensionForm(templates?.items?.length, 'templates')}
              </Typography>

              <EnhancedTable
                rows={templates?.items || []}
                headCells={HEAD_CELLS}
                filter={filter}
                total={templates?.total || 0}
                fetching={fetching}
                heightRow={77}
                setFilter={setFilter}
              >
                <TemplatesTableBody
                  data={templates?.items}
                  setCurID={setCurID}
                  setIsSuccessModal={handleArchiver}
                  isViewableItems={isAllowedToRetrieveMailingTemplates}
                  isEditableItem={isAllowedToUpdateMailingTemplates}
                  isBackToWorkItem={isAllowedToArchiveMailingTemplates}
                  isDeletableItem={isAllowedToDeleteMailingTemplates}
                />
              </EnhancedTable>
            </>
            ) : (
              <NotFoundItems/>
            )
        ) : (
          <NotFoundItems message="Вам запрещён доступ к просмотру списка событий" />
        )
      }

      <CustomModal
        opened={isSuccessModal}
        modalClose={() => {
          dispatch(clearArchiveTemplateErrors());
          setIsSuccessModal(false);
        }}
        text="Данный шаблон используется!"
        subText={archiveError}
        withoutSubmit={true}
        data-testid="Warning Modal"
      />
    </Box>
  );
};
